import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  const syncLanguage = useCallback((userData) => {
    if (userData?.preferred_language) {
      i18n.changeLanguage(userData.preferred_language);
      localStorage.setItem("lv_language", userData.preferred_language);
    }
  }, [i18n]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncLanguage(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [syncLanguage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    syncLanguage(data);
    return data;
  };

  const register = async (email, password, name, agencyName) => {
    const { data } = await api.post("/auth/register", {
      email, password, name, agency_name: agencyName,
    });
    setUser(data);
    syncLanguage(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
